import { BfiApi, IApiResponse } from './bfiApi';
import { ISearchCriteria } from './common';
import {
    ExperienceProjectType,
    ExperiencePropertyTwoToFourUnitStructureType,
    ExperiencePropertyType,
    ExperienceRoleType,
    ExperienceVerificationStatusType,
    RuleGridType,
    ExperienceTotalType,
    ExperienceGrandTotalType,
    ExperienceDateType,
} from '../../../constants/src';
import { ExperienceType } from '../../../constants/src';
import { IAddress } from '@apply/store/src/cross';

export interface IPaging {
    skip: number;
    take: number;
}

export interface IExperienceLevelHistoryCriteria extends ISearchCriteria {
    levelid: number;
}

export interface IExperienceHistoryCriteria extends ISearchCriteria {
    experienceId: number;
}

export interface IExperienceRuleHistoryCriteria extends ISearchCriteria {
    ruleid: number;
}

export interface IExperienceSupplementalRuleHistoryCriteria extends ISearchCriteria {
    supplementalRuleId: number;
}

export interface IExperienceLevelChangeEntry {
    ChangeType: string;
    SectionName: string;
    FieldName: string;
    FromValue: string;
    ToValue: string;
}

export interface IExperienceLevelChangeHistoryDetail {
    id: number;
    dateAndTime: Date;
    userName: string;
    reason: string;
    notes: string;
    entries: [IExperienceLevelChangeEntry];
}

export interface IExperienceLevelChangesHistoryGridData {
    id: number;
    dateAndTime: Date;
    userName: string;
    reason: string;
    notes: string;
}

export interface IExperienceRuleChangeEntry {
    ChangeType: string;
    SectionName: string;
    FieldName: string;
    FromValue: string;
    ToValue: string;
}

export interface IExperienceRuleChangeHistoryDetail {
    id: number;
    dateAndTime: Date;
    userName: string;
    reason: string;
    notes: string;
    entries: [IExperienceRuleChangeEntry];
}

export interface IExperienceRuleChangesHistoryGridData {
    id: number;
    dateAndTime: Date;
    userName: string;
    reason: string;
    notes: string;
}

export interface IExperienceSupplementalRuleChangeEntry {
    ChangeType: string;
    SectionName: string;
    FieldName: string;
    FromValue: string;
    ToValue: string;
}

export interface IExperienceSupplementalRuleChangeHistoryDetail {
    id: number;
    dateAndTime: Date;
    userName: string;
    reason: string;
    notes: string;
    entries: [IExperienceSupplementalRuleChangeEntry];
}

export interface IExperienceSupplementalRuleChangesHistoryGridData {
    id: number;
    dateAndTime: Date;
    userName: string;
    reason: string;
    notes: string;
}

export interface IRuleGridSearchRequest extends ISearchCriteria {
    gridType: RuleGridType;
}

export interface IExperienceRulesGridResult {
    id: number;
    ruleBorrowerTypeId: number;
    roleTypeId: number;
    propertyStructureTypeId: number;
    ruleCategorizationTypeId: number;
    ruleDescription: string;
    groundUpConstructionCredits: number;
    buildRentCredits: number;
    majorRehabCredits: number;
    lightRehabCredits: number;
    rentalCredits: number;
}

export interface IExperienceRule {
    id: number;
    ruleBorrowerTypeId: number;
    roleTypeId: number;
    ruleCategorizationId: number;
    propertyStructureTypeId?: number;
    groundUpConstructionCredits?: number;
    buildRentCredits?: number;
    majorRehabCredits?: number;
    lightRehabCredits?: number;
    rentalCredits?: number;
}

export interface IExperienceSupplementalRule {
    id: number;
    supplementalRuleIdentifierTypeId: number;
    questionText: string;
    credits: number;
}

export interface IExperienceSupplementalRulesGridResult {
    id: number;
    supplementalRuleIdentifierTypeId: number;
    questionText: string;
    credits: number;
}

export interface IExperienceLevelsGridResult {
    id: number;
    soldHomes: number;
    bfiRepayments: number;
    maxLTC: number;
    maxLTV: number;
    notes: string;
}

export interface IExperienceLevel {
    id: number;
    levelName: string;
    soldHomesOperatorId: number;
    soldHomes: number;
    bfiRepaymentsOperatorId: number;
    bfiRepayments: number;
    maxLTC: number;
    maxLTV: number;
    notes: string;
}

export interface INonBfiExperienceDetail {
    id: number;
    experienceTypeId: ExperienceType;
    principalId: number;
    entityId: number;
    percentOwnership: number;
    projectTypeId?: ExperienceProjectType;
    propertyTypeId?: ExperiencePropertyType;
    twoToFourUnitTypeId?: ExperiencePropertyTwoToFourUnitStructureType;
    unitCount?: number;
    address: IAddress;
    stillOwn: boolean;
    dateSold?: Date;
    dateSoldTypeId?: ExperienceDateType;
    salesPrice?: number;
    datePurchased?: Date;
    purchasePrice?: number;
    notes?: string;
    sourceWebsite?: string;
    sourceUrl?: string;
    verificationStatusTypeId: ExperienceVerificationStatusType;
    verificationNotes?: string;
    verifiedDate?: Date;
    verifiedById?: number;
    credits?: number;
    conditionedSF?: number;
    budget: number;
    roleTypeId?: ExperienceRoleType;
    otherRoleName: string;
}

export interface INonBfiExperienceSearchCriteria extends ISearchCriteria {
    verificationStatusTypes: [ExperienceVerificationStatusType];
}

export interface IBfiOutstandingExperienceSearchCriteria extends ISearchCriteria {
    projectTypes: [ExperienceProjectType];
}

export interface IExperienceNonBfiGridResult extends IAddress {
    id: number;
    highlight: boolean;
    projectTypeName: string;
    projectTypeId: number;
    stillOwn: boolean;
    dateSold?: Date;
    salesPrice?: number;
    units?: number;
    condition: string;
    datePurchased?: Date;
    purchasePrice?: number;
    ownedAs: string;
    verificationStatusTypeId: ExperienceVerificationStatusType;
    budget?: number;
    propertyTypeId?: ExperiencePropertyType;
}

export interface IExperienceBfiRepaidGridResult extends IAddress {
    id: number;
    projectTypeId: number;
    bfiNumber: string;
    lastPayDate: Date;
    repaidAmount: number;
    monthsOutstanding: number;
    committed: number;
    appraisedValue: number;
    ltv: number;
    salesPrice?: number;
    unitCount: number;
    isMajorRehab: boolean;
    isTwentyPercentPlusOwner: boolean;
    structureTypeId?: number;
}

export interface IExperienceBfiOutstandingGridResult extends IAddress {
    id: number;
    projectTypeId: number;
    bfiNumber: string;
    noteDate: Date;
    monthsOutstanding: number;
    committed: number;
    outstandingAmount: number;
    appraisedValue: number;
    ltv: number;
    appraisedDate?: Date;
    unitCount: number;
    isMajorRehab: boolean;
    isTwentyPercentPlusOwner: boolean;
    structureTypeId?: number;
}

export interface IExperienceNonBfiValidation {
    id: number;
    principalId: number;
    address: IAddress;
}

export interface IExperienceLevelNamesValidationResponse extends Omit<IApiResponse, 'result'> {
    result: boolean;
}

export interface IExperienceLevelName {
    levelName: string;
    ignoreId: number;
}

export interface ISuggestedVerificationWebsiteLinkCriteria {
    state: string;
    county: string;
}

export interface INonBfiScoreRequest {
    verificationStatusTypeId?: number | null | undefined;
    roleTypeId?: number | null | undefined;
    projectTypeId?: number | null | undefined;
    propertyTypeId?: number | null | undefined;
    twoToFourUnitTypeId?: number | null | undefined;
    budget?: number | null | undefined;
}

export interface IExperienceLevelsGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceLevelsGridResult];
    };
}

export interface IExperienceRuleResponse extends Omit<IApiResponse, 'result'> {
    result: IExperienceRule;
}

export interface IExperienceSupplementalRuleResponse extends Omit<IApiResponse, 'result'> {
    result: IExperienceSupplementalRule;
}

export interface IExperienceLevelResponse extends Omit<IApiResponse, 'result'> {
    result: IExperienceLevel;
}

export interface IExperienceLevelChangesHistoryGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceLevelChangesHistoryGridData];
    };
}

export interface IExperienceLevelChangesHistoryDetailResponse extends Omit<IApiResponse, 'result'> {
    result: IExperienceLevelChangeHistoryDetail;
}

export interface IExperienceRulesGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceRulesGridResult];
    };
}

export interface IExperienceRuleChangesHistoryGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceRuleChangesHistoryGridData];
    };
}

export interface IExperienceRuleChangesHistoryDetailResponse extends Omit<IApiResponse, 'result'> {
    result: IExperienceRuleChangeHistoryDetail;
}

export interface IExperienceSupplementalRulesGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceSupplementalRulesGridResult];
    };
}

export interface IExperienceSupplementalRuleChangesHistoryGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceSupplementalRuleChangesHistoryGridData];
    };
}

export interface IExperienceSupplementalRuleChangesHistoryDetailResponse extends Omit<IApiResponse, 'result'> {
    result: IExperienceSupplementalRuleChangeHistoryDetail;
}

export interface IExperienceNonBfiGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceNonBfiGridResult];
    };
}

export interface IExperienceBfiRepaidGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceBfiRepaidGridResult];
    };
}

export interface IExperienceBfiOutstandingGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceBfiOutstandingGridResult];
    };
}

export interface IExperienceNonBfiCreditsResponse extends Omit<IApiResponse, 'result'> {
    result: number;
}

export interface IExperience20PercentOwnerTotalGridResult {
    principalId: number;
    experienceTotalTypeId: ExperienceTotalType;
    constructionAmount: number;
    majorRehabAmount: number;
    fixFlipAmount: number;
    rentalAmount: number;
    sortOrder?: number;
}

export interface IExperienceNot20PercentOwnerTotalGridResult {
    principalId: number;
    experienceTotalTypeId: ExperienceTotalType;
    constructionAmount: number;
    majorRehabAmount: number;
    fixFlipAmount: number;
    rentalAmount: number;
    sortOrder?: number;
}

export interface IExperienceSuggestedVerifiectionWebsiteLinkResult {
    id: number;
    verifiedByName: string;
    verifiedDate?: Date;
    verifiedById?: number;
    sourceWebsite: string;
    sourceUrl: string;
}

export interface IExperiencePrincipalLevelResult {
    Id: number;
    LevelName: string;
}

export interface IExperienceGrandtotalsGridResult {
    principal: number;
    expenceGrandTotalTypeId: ExperienceGrandTotalType;
    experienceTotalAmount: number;
}

export interface IExperience20PercentOwnerTotalGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperience20PercentOwnerTotalGridResult];
    };
}

export interface IExperienceNot20PercentOwnerTotalGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceNot20PercentOwnerTotalGridResult];
    };
}

export interface IExperienceGrandTotalsGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IExperienceGrandtotalsGridResult];
    };
}

export interface IExperiencePrincipalLevelResponse extends Omit<IApiResponse, 'result'> {
    result: [IExperiencePrincipalLevelResult];
}

export interface IExperienceSuggestedVerifiectionWebsiteLinkResponse extends Omit<IApiResponse, 'result'> {
    result: [IExperienceSuggestedVerifiectionWebsiteLinkResult];
}

export class ExperienceApi extends BfiApi {
    //Rules
    getTwentyPercentResidentialRules(criteria: IRuleGridSearchRequest): Promise<IExperienceRulesGridResponse> {
        return super.post(`/experiences/grid/rules/twentypercentresidential`, criteria);
    }

    getTwentyPercentCommercialRules(criteria: IRuleGridSearchRequest): Promise<IExperienceRulesGridResponse> {
        return super.post(`/experiences/grid/rules/twentypercentcommercial`, criteria);
    }

    getLicensedGCRules(criteria: IRuleGridSearchRequest): Promise<IExperienceRulesGridResponse> {
        return super.post(`/experiences/grid/rules/licensedgc`, criteria);
    }

    getRule(id: number): Promise<IExperienceRuleResponse> {
        return super.get(`/experiences/rules/${id ?? ''}`);
    }

    addUpdateRule(rule: IExperienceRule): Promise<IExperienceRuleResponse> {
        return super.post('/experiences/rules', rule);
    }

    getRuleChangeHistory(criteria: IExperienceRuleHistoryCriteria): Promise<IExperienceRuleChangesHistoryGridResponse> {
        return super.post(`/experiences/rules/rulehistory`, criteria);
    }

    getRuleChangeHistoryDetail(changeId: number): Promise<IExperienceRuleChangesHistoryDetailResponse> {
        return super.get(`/experiences/rules/rulehistory/${changeId}`);
    }

    //Supplemental Rules
    getSupplementalRules(criteria: ISearchCriteria): Promise<IExperienceSupplementalRulesGridResponse> {
        return super.post('/experiences/grid/supplementalrules', criteria);
    }

    getSupplementalRule(id: number): Promise<IExperienceSupplementalRuleResponse> {
        return super.get(`/experiences/supplementalrules/${id ?? ''}`);
    }

    addUpdateSupplementalRule(rule: IExperienceSupplementalRule): Promise<IExperienceSupplementalRuleResponse> {
        return super.post('/experiences/supplementalrules', rule);
    }

    getSupplementalRuleChangeHistory(
        criteria: IExperienceSupplementalRuleHistoryCriteria
    ): Promise<IExperienceSupplementalRuleChangesHistoryGridResponse> {
        return super.post(`/experiences/supplementalrules/rulehistory`, criteria);
    }

    getSupplementalRuleChangeHistoryDetail(changeId: number): Promise<IExperienceSupplementalRuleChangesHistoryDetailResponse> {
        return super.get(`/experiences/supplementalrules/rulehistory/${changeId}`);
    }

    //Levels
    async getLevels(criteria: ISearchCriteria): Promise<IExperienceLevelsGridResponse> {
        return super.post(`/experiences/grid/levels`, criteria);
    }

    getLevel(id: number): Promise<IExperienceLevelResponse> {
        return super.get(`/experiences/levels/${id ?? ''}`);
    }

    validateLevelName(name: IExperienceLevelName): Promise<IExperienceLevelNamesValidationResponse> {
        return super.post('/experiences/levels/validatename', name);
    }

    addUpdateLevel(level: IExperienceLevel): Promise<IExperienceLevelResponse> {
        return super.post('/experiences/levels', level);
    }

    getLevelChangeHistory(criteria: IExperienceLevelHistoryCriteria): Promise<IExperienceLevelChangesHistoryGridResponse> {
        return super.post(`/experiences/levels/levelhistory`, criteria);
    }

    getLevelChangeHistoryDetail(changeId: number): Promise<IExperienceLevelChangesHistoryDetailResponse> {
        return super.get(`/experiences/levels/levelhistory/${changeId}`);
    }

    getExperienceNonBfi(experienceId: number): Promise<IApiResponse> {
        return super.get(`/experiences/nonbfi/${experienceId}`);
    }

    getExperienceBfi(experienceId: string): Promise<IApiResponse> {
        return super.get(`/experiences/bfi/${experienceId}`);
    }

    getAddresses(principalId: number): Promise<IApiResponse> {
        return super.get(`/experiences/addresses/principals/${principalId}`);
    }

    updateExperienceNonBfi(model: INonBfiExperienceDetail): Promise<IApiResponse> {
        return super.post(`/experiences/nonbfi`, model);
    }

    deleteExperienceNonBfi(experienceId: number): Promise<IApiResponse> {
        return super.delete(`/experiences/nonbfi/${experienceId}`);
    }

    validateExperienceNonBfi(model: IExperienceNonBfiValidation): Promise<IApiResponse> {
        return super.post(`/experiences/nonbfi/validate`, model);
    }

    getSuggestedVerificationWebsiteLinks(
        criteria: ISuggestedVerificationWebsiteLinkCriteria
    ): Promise<IExperienceSuggestedVerifiectionWebsiteLinkResponse> {
        return super.post(`experiences/nonbfi/verification/suggestedwebsites`, criteria);
    }

    getAuditHistory(experienceId: number): Promise<IApiResponse> {
        return super.get(`/experiences/${experienceId}/auditing`);
    }

    getAuditHistoryDetail(changeId: number): Promise<IApiResponse> {
        return super.get(`/experiences/auditing/${changeId}`);
    }

    getExperiencesNonBfiGrid(
        principalId: number,
        criteria: INonBfiExperienceSearchCriteria
    ): Promise<IExperienceNonBfiGridResponse> {
        return super.post(`/experiences/nonbfi/principals/${principalId}/grid`, criteria);
    }

    getExperiencesBfiRepaidGrid(principalId: number, criteria: ISearchCriteria): Promise<IExperienceBfiRepaidGridResponse> {
        return super.post(`/experiences/bfirepaid/principals/${principalId}/grid`, criteria);
    }

    getExperiencesBfiOutstandingGrid(
        principalId: number,
        criteria: IBfiOutstandingExperienceSearchCriteria
    ): Promise<IExperienceBfiOutstandingGridResponse> {
        return super.post(`/experiences/bfioutstanding/principals/${principalId}/grid`, criteria);
    }

    getNonBfiCredits(model: INonBfiScoreRequest): Promise<IExperienceNonBfiCreditsResponse> {
        return super.post(`/experiences/nonbfi/credits`, model);
    }

    getExperience20PercentOwnerTotals(principalId: number): Promise<IExperience20PercentOwnerTotalGridResponse> {
        return super.post(`/experiences/principals/${principalId}/20percentowner/grid`);
    }

    getExperienceNot20PercentOwnerTotals(principalId: number): Promise<IExperienceNot20PercentOwnerTotalGridResponse> {
        return super.post(`/experiences/principals/${principalId}/not20percentowner/grid`);
    }

    getExperienceGrandTotals(principalId: number): Promise<IExperienceGrandTotalsGridResponse> {
        return super.post(`/experiences/principals/${principalId}/grandtotals/grid`);
    }

    getExperienceLevel(principalId: number): Promise<IExperiencePrincipalLevelResponse> {
        return super.post(`/experiences/principals/${principalId}/level`);
    }

    async getPrincicipalExperienceLevels(criteria: ISearchCriteria): Promise<IExperienceLevelsGridResponse> {
        return super.post(`/experiences/principals/levels/grid`, criteria);
    }
}
